import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@readcloud/component-library';
import {
  Annotation,
  Book,
  EnhancedAnnotation,
  PathAnnotation,
  Tag,
} from '@readcloud/data';
import React from 'react';
import {
  annotationContainers,
  createEmptyAnnotation,
} from '../helpers/createEmptyAnnotations';
import { getPointOfEvent } from '../utils';
import { AnnotationEditor } from './AnnotationEditor';
import { AnnotationsContext } from './AnnotationsContext';
import { AnnotationViewer } from './AnnotationViewer';
import { PathAnnotationDrawer } from './PathAnnotation/PathAnnotationDrawer';
import { boundingRectOfPaths } from './rectUtils';

const rndStyle: React.CSSProperties = {
  opacity: 0.9,
};
/**
 * Render this component to be able to create an annotation within an area, by clicking and dragging, or placing....unsure of the best UX as of now.
 * Its responsibility is to be able to create annotations interactively, instead of using say a form that just creates an object to update state with.
 */
export const AnnotationCreationStage = (props: {
  author: string;
  book: string;
  color: string;
  pageNumber: number;
  scale: number;
  height: number;
  width: number;
  type: Annotation['type'];
  mediaContent: { mediaContent: Book[]; tagsContent: Tag[] };
  annoPrefs?: { [index: string]: Annotation['data'] };
  onSavePreferences?: (type: string, data: Partial<Annotation['data']>) => void;
  /**
   * This should allow the creation stage to create/update an annotation without submitting/saving it. For example, a drawing annotation that should be saved by the parent.
   * This was added to support Path/Drawing annotations, where a user will make a drawing, but will be saved only when the tool changes or the user leaves
   */
  /*     onUpsertLazyAnnotation: (annotation: OverlayAnnotations.Annotation) => void; */
  onCreateAnnotation: (annotation: Annotation) => void;
  editDialogTitle?: string;
  showPublisherOptions: boolean;
}) => {
  const minHeight =
    annotationContainers[props.type].config.minHeight * props.scale;
  const minWidth =
    annotationContainers[props.type].config.minWidth * props.scale;

  const containerRect = React.useRef<DOMRect>();
  const [selectionRect, setSelectionRect] = React.useState<{
    y: number;
    x: number;
    height: number;
    width: number;
  }>();
  const [currentAnnotation, setCurrentAnnotation] =
    React.useState<EnhancedAnnotation>();
  const latestCurrentAnnotation = React.useRef(currentAnnotation);
  latestCurrentAnnotation.current = currentAnnotation;

  const latestOnCreateAnnotation = React.useRef(props.onCreateAnnotation);
  latestOnCreateAnnotation.current = props.onCreateAnnotation;

  const [editing, setEditing] = React.useState(false);
  /**
   * Handle the case when type is "Path", so that we can setCurrentAnnotation to a new path annotation, and click+drag for selection rect isn't required
   */
  React.useEffect(() => {
    if (props.type === 'Path') {
      setCurrentAnnotation(
        createEmptyAnnotation({
          annoPrefs: props.annoPrefs,
          author: props.author,
          book: props.book,
          pageNumber: props.pageNumber,
          color: props.color,
          type: props.type,
          rect: {
            x: 0,
            y: 0,
            width: props.width / props.scale,
            height: props.height / props.scale,
          },
        })
      );

      return () => {
        if (
          latestCurrentAnnotation.current &&
          latestCurrentAnnotation.current.type === 'Path'
        ) {
          if (!latestCurrentAnnotation.current.data.paths.length) return;
          let boundingRect = boundingRectOfPaths(
            latestCurrentAnnotation.current.data.paths,
            latestCurrentAnnotation.current.data.strokeWidth,
            props.scale
          );

          latestOnCreateAnnotation.current({
            ...latestCurrentAnnotation.current,
            data: {
              ...latestCurrentAnnotation.current.data,
              rect: {
                height: boundingRect.height / props.scale,
                width: boundingRect.width / props.scale,
                x: boundingRect.x / props.scale,
                y: boundingRect.y / props.scale,
              },
              paths: latestCurrentAnnotation.current.data.paths.map((path) => ({
                ...path,
                points: path.points.map((point) => ({
                  x: point.x - boundingRect.x / props.scale,
                  y: point.y - boundingRect.y / props.scale,
                })),
              })),
            },
          });
        }
      };
    } else {
      setCurrentAnnotation(undefined);
      return;
    }
  }, [props.type]);
  const onPointerDown = React.useCallback(
    (
      e:
        | React.PointerEvent<HTMLElement>
        | React.MouseEvent<HTMLElement>
        | React.TouchEvent<HTMLElement>
    ) => {
      if (editing || !(e.currentTarget instanceof HTMLElement)) return;
      if (currentAnnotation) {
        setEditing(true);
        return;
      }
      const point = getPointOfEvent(e);

      const parentRect = (containerRect.current =
        e.currentTarget.getBoundingClientRect());
      const initialHeight = minHeight;
      const initialWidth = minWidth;

      const relativeMouseX = point.x - parentRect.left;
      const relativeMouseY = point.y - parentRect.top;

      const bottom = Math.min(props.height, relativeMouseY + initialHeight);
      const right = Math.min(props.width, relativeMouseX + initialWidth);

      const x = right - initialWidth;
      const y = bottom - initialHeight;

      const selectionRect = {
        height: 0,
        width: 0,
        x,
        y,
      };
      const annoRect = {
        height: initialHeight,
        width: initialWidth,
        x,
        y,
      };
      const initialAnnotation = createEmptyAnnotation({
        annoPrefs: props.annoPrefs,
        author: props.author,
        book: props.book,
        pageNumber: props.pageNumber,
        color: props.color,
        type: props.type,
        rect: annoRect,
      });
      setSelectionRect(selectionRect);
      setCurrentAnnotation(initialAnnotation);
    },
    [
      currentAnnotation,
      props.author,
      props.book,
      props.color,
      props.pageNumber,
      editing,
      props.height,
      props.width,
      props.type,
    ]
  );
  const onPointerMove = React.useCallback(
    (
      e:
        | React.PointerEvent<HTMLElement>
        | React.MouseEvent<HTMLElement>
        | React.TouchEvent<HTMLElement>
    ) => {
      if (editing || !containerRect.current) return;
      e.preventDefault();
      const point = getPointOfEvent(e);
      const relativeMouseX = point.x - containerRect.current.left;
      const relativeMouseY = point.y - containerRect.current.top;

      setSelectionRect((selectionRect) => {
        if (selectionRect) {
          return {
            height: relativeMouseY - selectionRect.y,
            width: relativeMouseX - selectionRect.x,
            x: selectionRect.x,
            y: selectionRect.y,
          };
        } else {
          return selectionRect;
        }
      });

      setCurrentAnnotation((currentAnno) => {
        if (currentAnno && currentAnno.data && 'rect' in currentAnno.data) {
          const maxHeight = props.height - currentAnno.data.rect.y;
          const maxWidth = props.width - currentAnno.data.rect.x;
          const snappedRect = {
            height: Math.min(
              maxHeight,
              Math.max(minHeight, relativeMouseY - currentAnno.data.rect.y)
            ),
            width: Math.min(
              maxWidth,
              Math.max(minWidth, relativeMouseX - currentAnno.data.rect.x)
            ),
            x: currentAnno.data.rect.x,
            y: currentAnno.data.rect.y,
          };
          return {
            ...currentAnno,
            data: {
              ...currentAnno.data,
              rect: snappedRect,
            },
          } as EnhancedAnnotation;
        } else {
          return currentAnno;
        }
      });
    },
    [editing, props.height, props.width]
  );

  const onPointerCancel = React.useCallback(() => {
    if (editing) return;
    containerRect.current = undefined;
    setSelectionRect(undefined);
    setCurrentAnnotation(undefined);
  }, [editing]);

  const handleSubmitAnnotation = React.useCallback(
    (anno: Annotation) => {
      if (!anno.data || !('rect' in anno.data)) return;
      setEditing(false);
      setCurrentAnnotation(undefined);
      setSelectionRect(undefined);
      props.onCreateAnnotation({
        ...anno,
        data: {
          ...anno.data,
          rect: {
            x: anno.data.rect.x / props.scale,
            y: anno.data.rect.y / props.scale,
            height: anno.data.rect.height / props.scale,
            width: anno.data.rect.width / props.scale,
          },
        },
      } as Annotation);
    },
    [props.onCreateAnnotation, props.scale]
  );

  const onPointerUp = React.useCallback(
    (
      e:
        | React.PointerEvent<HTMLElement>
        | React.MouseEvent<HTMLElement>
        | React.TouchEvent<HTMLElement>
    ) => {
      if (editing || !currentAnnotation) return;
      containerRect.current = undefined;
      setSelectionRect(undefined);
      if (annotationContainers[props.type].config.requiresCreationForm) {
        setEditing(true);
      } else {
        handleSubmitAnnotation(currentAnnotation);
      }
    },
    [editing, handleSubmitAnnotation, currentAnnotation, props.type]
  );

  const handlePathChange = React.useCallback((anno: PathAnnotation) => {
    setCurrentAnnotation({
      ...anno,
      data: {
        ...anno.data,
        scaleWithPage: true,
      },
    } as EnhancedAnnotation);
  }, []);

  const handleCancel = React.useCallback(() => {
    setEditing(false);
    setSelectionRect(undefined);
    setCurrentAnnotation(undefined);
  }, []);

  const mediaCtx = React.useMemo(
    () => ({
      ...props.mediaContent,
    }),
    [props.mediaContent]
  );

  return (
    <AnnotationsContext.Provider value={mediaCtx}>
      <>
        <div
          className="rc-relative"
          style={{ height: props.height, width: props.width }}
        >
          <Modal size="lg" isOpen={!!currentAnnotation && editing} backdrop>
            <ModalHeader>
              <ModalTitle>
                {props.editDialogTitle || 'Create Annotation'}
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              {currentAnnotation && (
                <AnnotationEditor
                  onCancel={handleCancel}
                  submitLabel="Create"
                  annotation={currentAnnotation}
                  onChange={handleSubmitAnnotation}
                  showPublisherOptions={props.showPublisherOptions}
                  onSavePreferences={props.onSavePreferences}
                  annoPrefs={props.annoPrefs}
                />
              )}
            </ModalBody>
          </Modal>

          {currentAnnotation &&
          currentAnnotation.data &&
          currentAnnotation.type !== 'Path' ? (
            <div
              className="rc-absolute"
              style={{
                ...rndStyle,
                top: currentAnnotation.data.rect.y,
                left: currentAnnotation.data.rect.x,
                height: currentAnnotation.data.rect.height,
                width: currentAnnotation.data.rect.width,
              }}
            >
              <AnnotationViewer
                selected={false}
                editing
                onChange={handleSubmitAnnotation}
                scale={props.scale}
                dragging={false}
                height={currentAnnotation.data.rect.height}
                width={currentAnnotation.data.rect.width}
                annotation={currentAnnotation}
              />
            </div>
          ) : null}
          {selectionRect ? (
            <div
              className="rc-absolute"
              style={{
                top: selectionRect.y,
                left: selectionRect.x,
                height: selectionRect.height,
                width: selectionRect.width,
                border: 'dashed 1px #ccc',
              }}
            />
          ) : undefined}
          {currentAnnotation && currentAnnotation.type === 'Path' ? (
            <div
              className="rc-absolute"
              style={{
                top: 0,
                left: 0,
                height: props.height,
                width: props.width,
              }}
            >
              <PathAnnotationDrawer
                scale={props.scale}
                height={props.height}
                width={props.width}
                annotation={currentAnnotation}
                onChange={handlePathChange}
              />
            </div>
          ) : null}

          {props.type !== 'Path' ? (
            <div
              style={{
                userSelect: 'none',
                cursor: 'crosshair',
                top: 0,
                left: 0,
                height: props.height,
                width: props.width,
                position: 'absolute',
                touchAction: 'none', //IMPORTANT - this makes events work on mobile, otherwise they cancel
              }}
              onTouchStart={onPointerDown}
              onMouseDown={onPointerDown}
              onTouchEnd={onPointerUp}
              onMouseUp={onPointerUp}
              onMouseMove={onPointerMove}
              onTouchMove={onPointerMove}
              onTouchCancel={onPointerCancel}
            />
          ) : null}
        </div>
      </>
    </AnnotationsContext.Provider>
  );
};
